// Need to use the React-specific entry point to import createApi
import { createApi } from '@reduxjs/toolkit/query/react'
import type { WordInfo } from '@tunasong/models'
import { logger } from '@tunasong/models'
import invariant from 'tiny-invariant'
import z from 'zod'
import baseQuery from './base-query.js'
import { getAgentDispatcher } from './agent-client-tool-registry.js'
import { partsToToolCalls } from './parts-to-tool-calls.js'
import type {
  Audio,
  BlockRef,
  Image,
  MelodySuggestionResponse,
  NoteName,
  Persisted,
  Scale,
} from '@tunasong/schemas'

/** Chat types inlined to avoid circular dependency on @tunasong/ai-lib */
export interface AIBasicModelMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content: string
}

/** ElevenLabs music model. Inlined (see above) to avoid the ai-lib cycle;
 * kept in sync with `elevenLabsMusicModels` in `@tunasong/ai-lib`. */
export type ElevenLabsMusicModel = 'music_v1' | 'music_v2'

export interface ChatMessage {
  role: 'user' | 'system' | 'assistant'
  content: string
  function_call?: { name: string; arguments: string } | null
}

// `ToolCallSummary` was moved to `./tool-call-summary.js` so the type can be
// shared with `parts-to-tool-calls.ts` without producing a circular import
// between this file and the reducer it consumes (`nlp.ts` → reducer → nlp.ts).
// Imported for in-module use and re-exported so existing public callers
// continue to import it from the `@tunasong/redux/nlp` surface unchanged.
import type { ToolCallSummary } from './tool-call-summary.js'
export type { ToolCallSummary }

export interface ChatMessageStream extends ChatMessage {
  pending?: boolean
  /** Terminal error message. Set by the streaming endpoint when fetch rejects. */
  error?: string
  /**
   * Raw Vercel UI Message Stream parts (`UIMessageChunk`), accumulated in
   * arrival order. Populated by `agentStream`; non-parts streams (e.g.
   * `chatStream`) leave this undefined.
   */
  parts?: unknown[]
  /**
   * Compact tool-call projection derived from the parts stream. Populated
   * by `agentStream`; non-parts streams leave this undefined.
   */
  toolCalls?: ToolCallSummary[]
}

export interface ChatPayload {
  temperature?: number
  providerName?: string
  prompt: ChatMessage[]
  functions?: unknown[]
  model?: string
  parentChatId?: string
}

export interface AgentStreamPayload {
  agentId: string
  message?: string
  trigger?: { type: 'chat' | 'change' | 'idle' | 'goal' | 'session'; reason?: string; at?: string }
  /**
   * Block the user is asking about. Lambda's input schema accepts this and
   * threads it through AgentContextInputs.focus and ToolCallContext.focus.
   * Optional — fresh chat without a target still works.
   */
  focus?: BlockRef & { sourceAgentId?: string }
  model?: string
  temperature?: number
  /**
   * Required. Unique per submit so RTK Query cache keys don't collapse across `Ask again`.
   * Lambda's `z.object().safeParse` strips unknown keys; this is a frontend-only contract.
   */
  requestId: string
  /**
   * Stable per-turn id. Required by the Lambda for turn-state persistence
   * (Phase 3). On a fresh turn, callers generate a UUID; the handler echoes
   * the same value on continuation requests for the same turn.
   */
  turnId?: string
  /**
   * Continuation: client's reply with results for the pending client-tool
   * calls from the prior request on this same turnId. Set internally by the
   * agentStream handler — callers should not provide this for the initial
   * request.
   */
  continuation?: {
    toolResults: { toolCallId: string; toolName: string; output: unknown }[]
  }
}

export interface ChatResponse {
  completions: { message: ChatMessage; finish_reason: string }[]
  text: string
  tokens: number
}

export interface NLP {
  syllables?: Record<
    string,
    {
      text: string
      syllables: string[]
      count: number
    }
  >
  nextWord: [{ score: number; token_str: string; sequence: string }]
}

/** @todo Zod schema */
export const NLPImageResponseSchema = z.object({
  filename: z.string(),
})
export type NLPImageResponse = z.infer<typeof NLPImageResponseSchema>

/** @todo refactor these */
export type NLPMessageSchema = ChatMessage
export type ChatCompletionRequestMessage = ChatPayload
export type NLPCompleteResponse = ChatResponse

export const NLPEmbeddingsResponseSchema = z.object({
  tokens: z.number(),
  data: z.array(z.array(z.number())),
})
export type NLPEmbeddingsResponse = z.infer<typeof NLPEmbeddingsResponseSchema>

// Define a service using a base URL and expected endpoints
export const nlpApi = createApi({
  reducerPath: 'nlp-api',
  baseQuery,
  endpoints: builder => ({
    getNlp: builder.query<NLP, { text: string; offset?: number; nextText?: string; prevText?: string }>({
      query: ({ text, offset = 0, nextText = '', prevText = '' }) =>
        `nlp/?text=${text}&offset=${offset}&nextText=${nextText}&prevText=${prevText}`,
    }),
    getWord: builder.query<WordInfo, { word: string }>({
      query: ({ word }) => `words/${word}`,
    }),
    complete: builder.query<
      NLPCompleteResponse,
      { prompt?: unknown[] | string; temperature?: number; model?: string; providerName?: string }
    >({
      query: ({ prompt, model, temperature, providerName }) => ({
        url: `nlp/complete`,
        method: 'POST',
        body: { prompt, temperature, model, providerName },
      }),
    }),
    /** Streaming complete call */
    completeStream: builder.query<
      { pending: boolean; text: string } | null,
      {
        prompt?: unknown[] | string
        temperature?: number
        model?: string
        providerName?: string
        // Use to ensure the cache is unique
        requestId: string
      }
    >({
      queryFn: async () => ({ data: null }),
      async onCacheEntryAdded(payload, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }) {
        // create a fetch request to the server to initiate a socket connection
        // @see https://developer.chrome.com/articles/fetch-streaming-requests/

        if (!payload) {
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = (getState() as any).user.authData.idToken.jwtToken
        invariant(token, 'No token found')

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const streamingApiBaseUrl = (getState() as any).config.streamingApiBaseUrl

        try {
          await cacheDataLoaded

          const response = await fetch(`${streamingApiBaseUrl}/complete/`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          })

          const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader()
          invariant(reader, 'Reader is not defined')

          while (true) {
            const { value, done } = await reader.read()
            console.log('NLP API', { value, done })

            updateCachedData(draft => {
              const msg = draft ?? {
                text: '',
                pending: true,
              }
              const updatedCacheData = {
                ...msg,
                text: `${msg.text}${value ?? ''}`,
                pending: !done,
              }
              return updatedCacheData
            })

            if (done) {
              break
            }
          }
        } catch (e) {
          logger.error(e)
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
      },
    }),
    embeddings: builder.query<NLPEmbeddingsResponse, { text: string }>({
      query: ({ text }) => ({
        url: `nlp/embeddings`,
        method: 'POST',
        body: { text },
      }),
    }),
    chat: builder.query<NLPCompleteResponse, { prompt: ChatCompletionRequestMessage[] | string }>({
      query: ({ prompt }) => ({
        url: `nlp/chat`,
        method: 'POST',
        body: { prompt },
      }),
    }),
    transcribe: builder.query<{ language: string; text: string }, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `nlp/transcribe`,
        method: 'POST',
        body: { entityId },
      }),
    }),
    chatStream: builder.query<ChatMessageStream | null, ChatPayload>({
      queryFn: async () => ({ data: null }),
      async onCacheEntryAdded(payload, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }) {
        // create a fetch request to the server to initiate a socket connection
        // @see https://developer.chrome.com/articles/fetch-streaming-requests/

        if (!payload) {
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = (getState() as any).user.authData.idToken.jwtToken
        invariant(token, 'No token found')

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const streamingApiBaseUrl = (getState() as any).config.streamingApiBaseUrl

        try {
          await cacheDataLoaded

          const response = await fetch(`${streamingApiBaseUrl}/chat/`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader()
          invariant(reader, 'Reader is not defined')

          while (true) {
            const { value = '', done } = await reader.read()

            updateCachedData(draft => {
              const msg = draft ?? {
                role: 'assistant',
                content: '',
                pending: true,
              }
              const updatedCacheData = {
                ...msg,
                content: `${msg.content}${value}`,
                pending: !done,
              }
              return updatedCacheData
            })

            if (done) {
              break
            }
          }
        } catch (e) {
          logger.error(e)
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
      },
    }),

    agentStream: builder.query<ChatMessageStream | null, AgentStreamPayload>({
      queryFn: async () => ({ data: null }),
      // Drop the cache entry the moment the last subscriber detaches — prevents
      // stale text from a previous submit flashing on a fresh `Ask again`.
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(payload, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }) {
        if (!payload) {
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = (getState() as any).user.authData.idToken.jwtToken
        invariant(token, 'No token found')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const streamingApiBaseUrl = (getState() as any).config.streamingApiBaseUrl

        const controller = new AbortController()
        void cacheEntryRemoved.then(() => controller.abort())

        // Hard cap on continuation round-trips per turn. Pairs with the dispatcher's
        // per-turn rate limit of 8 individual tool calls (allows multiple tools per
        // trip while still bounding the loop in case a buggy agent never stops
        // emitting client tools).
        const MAX_TRIPS = 4

        try {
          await cacheDataLoaded

          // The Lambda's bodySchema requires turnId (Phase 3). Default it here
          // so legacy callers (chord-advisor, sidebar agent commands) keep
          // working without a payload update — they don't run client tools but
          // still need a valid POST body.
          const turnId =
            payload.turnId ??
            (typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `t-${Date.now()}-${Math.random().toString(36).slice(2)}`)
          let body: AgentStreamPayload = { ...payload, turnId }
          const seenToolOutputs = new Set<string>()
          for (let trip = 0; trip < MAX_TRIPS; trip += 1) {
            const response = await fetch(`${streamingApiBaseUrl}/agent/`, {
              method: 'POST',
              body: JSON.stringify(body),
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            })

            const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader()
            invariant(reader, 'Reader is not defined')

            // Track pending client-tool calls for this trip — anything with a
            // tool-input-available chunk whose toolName is a registered client
            // tool AND no matching tool-output-available yet.
            const pendingClientCalls: { toolCallId: string; toolName: string; input: unknown }[] = []

            // Wire format: NDJSON of Vercel AI SDK `UIMessageChunk` values, one
            // per line. Buffer across reads (a chunk boundary can split a line)
            // and flush complete lines on each iteration.
            let buffer = ''
            while (true) {
              const { value = '', done } = await reader.read()
              buffer += value
              const newlineAt = buffer.lastIndexOf('\n')
              if (newlineAt >= 0) {
                const complete = buffer.slice(0, newlineAt)
                buffer = buffer.slice(newlineAt + 1)
                const lines = complete.split('\n')
                for (const line of lines) {
                  if (!line) {
                    continue
                  }
                  let part: unknown
                  try {
                    part = JSON.parse(line)
                  } catch {
                    continue
                  }
                  updateCachedData(draft => {
                    const msg = draft ?? { role: 'assistant', content: '', pending: true, parts: [] }
                    const nextParts = msg.parts ? [...msg.parts, part] : [part]
                    let nextContent = msg.content
                    if (typeof part === 'object' && part !== null) {
                      const typed = part as { type?: string; delta?: unknown }
                      if (typed.type === 'text-delta' && typeof typed.delta === 'string') {
                        nextContent = `${nextContent}${typed.delta}`
                      }
                    }
                    // Project tool-call lifecycle chunks into the stable
                    // ToolCallSummary list. We recompute on each part rather
                    // than maintaining an incremental reducer in the cache —
                    // parts arrays are tens-to-low-hundreds of entries in
                    // practice, well under any perf concern.
                    const nextToolCalls = partsToToolCalls(nextParts)
                    return {
                      ...msg,
                      content: nextContent,
                      parts: nextParts,
                      pending: true,
                      // Always set toolCalls (empty array when no tool parts yet)
                      // so the field is always present on the stream — consumers
                      // don't need to distinguish "no tool calls" from "key not
                      // yet populated".
                      toolCalls: nextToolCalls,
                    }
                  })
                  // Phase 3: detect client-tool calls. Lambda terminates after
                  // emitting tool-input-available for a client tool; no
                  // tool-output-available follows in the same stream.
                  if (typeof part === 'object' && part !== null) {
                    const typed = part as {
                      type?: string
                      toolCallId?: string
                      toolName?: string
                      input?: unknown
                    }
                    if (
                      typed.type === 'tool-input-available' &&
                      typed.toolCallId &&
                      typed.toolName &&
                      getAgentDispatcher(body.agentId) &&
                      !seenToolOutputs.has(typed.toolCallId)
                    ) {
                      pendingClientCalls.push({
                        toolCallId: typed.toolCallId,
                        toolName: typed.toolName,
                        input: typed.input,
                      })
                    }
                    if (typed.type === 'tool-output-available' && typed.toolCallId) {
                      seenToolOutputs.add(typed.toolCallId)
                    }
                  }
                }
              }
              if (done) {
                break
              }
            }

            // No pending client tools → terminal completion; mark and exit.
            if (pendingClientCalls.length === 0) {
              updateCachedData(draft => (draft ? { ...draft, pending: false } : draft))
              return
            }

            // Dispatch each client tool via the per-agent dispatcher. The dispatcher
            // resolver was set by plugins/agent when the AgentChat host mounted; if
            // the resolver is somehow gone now (HMR, race), bail out and surface the
            // partial state.
            const dispatcher = getAgentDispatcher(body.agentId)
            if (!dispatcher) {
              logger.warn('agent-stream: pending client tools but no dispatcher registered', {
                agentId: body.agentId,
                pending: pendingClientCalls.map(c => c.toolName),
              })
              updateCachedData(draft => (draft ? { ...draft, pending: false } : draft))
              return
            }

            const toolResults: { toolCallId: string; toolName: string; output: unknown }[] = []
            for (const call of pendingClientCalls) {
              const result = await dispatcher(call)
              toolResults.push({ toolCallId: call.toolCallId, toolName: call.toolName, output: result })
              // Project a synthetic tool-output-available part so the chip in the
              // sidebar flips to `called` immediately. The next trip's stream may
              // never emit one (the server would only do that for server tools).
              const syntheticPart = {
                type: 'tool-output-available' as const,
                toolCallId: call.toolCallId,
                output: result,
              }
              updateCachedData(draft => {
                const msg = draft ?? { role: 'assistant', content: '', pending: true, parts: [] }
                const nextParts = msg.parts ? [...msg.parts, syntheticPart] : [syntheticPart]
                return {
                  ...msg,
                  parts: nextParts,
                  pending: true,
                  toolCalls: partsToToolCalls(nextParts),
                }
              })
              seenToolOutputs.add(call.toolCallId)
            }

            // Build the continuation body — same agentId + turnId, no message, with
            // the freshly-dispatched tool results.
            body = {
              agentId: payload.agentId,
              turnId: payload.turnId,
              requestId: payload.requestId,
              continuation: { toolResults },
            }
          }

          // Hit the cap. Surface a soft error in the cache so the chip / panel can
          // explain to the user. Not a Sentry-worthy error — a buggy or chatty agent
          // can hit this naturally.
          logger.warn('agent-stream: continuation loop hit MAX_TRIPS', { trips: MAX_TRIPS, agentId: payload.agentId })
          updateCachedData(draft => ({
            role: 'assistant',
            content: draft?.content ?? '',
            parts: draft?.parts,
            toolCalls: draft?.toolCalls,
            pending: false,
            error: `Agent ran more than ${MAX_TRIPS} client-tool round-trips and was stopped.`,
          }))
        } catch (e) {
          if (controller.signal.aborted) {
            // Expected on unsubscribe — leave cache as-is; entry is about to be evicted.
            return
          }
          logger.error(e)
          updateCachedData(draft => ({
            role: 'assistant',
            content: draft?.content ?? '',
            pending: false,
            error: e instanceof Error ? e.message : 'fetch failed',
          }))
        }
      },
    }),

    image: builder.query<
      Persisted<Image>,
      {
        chatModel?: string
        size?: string
        name: string
        parentId: string
        prompt: ChatMessage[]
      }
    >({
      query: ({ size, chatModel, parentId, name, prompt }) => ({
        url: `nlp/image`,
        method: 'POST',
        body: { name, size, chatModel, parentId, prompt },
      }),
    }),
    generateMusic: builder.query<
      Persisted<Audio>,
      {
        name: string
        durationSec: number
        textModel?: string
        instrumental?: boolean
        /** ElevenLabs music model; defaults to v2 server-side when omitted. */
        modelId?: ElevenLabsMusicModel
        parentId: string
        prompt: AIBasicModelMessage[]
      }
    >({
      query: params => ({
        url: `nlp/music`,
        method: 'POST',
        body: params,
      }),
    }),
    /**
     * Melody suggestions (F.2.1) — see
     * `docs/superpowers/specs/2026-05-14-melody-suggestions-v1.md`.
     */
    melodySuggestions: builder.query<
      MelodySuggestionResponse,
      {
        scale: Scale
        chords: { root: NoteName; variant: string; position: number; duration: number }[]
        bpm?: number
        beatsPerMeasure?: number
        style?: string
      }
    >({
      query: params => ({
        url: `nlp/melody-suggestions`,
        method: 'POST',
        body: params,
      }),
    }),
  }),
})
